/**
 * LandingSection Component
 *
 * A hero section for the learning platform that showcases the platform's value proposition
 * with decorative elements and category tags. Derived from Figma design.
 */
import React from 'react';

import { LandingWrapper } from '../../components/features/landing/landingWrapper/LandingWrapper';
import { VisualSection } from '../../components/features/landing/visualSection/VisualSection';

export interface LandingSectionProps {
  /**
   * Main title text
   */
  title?: string;

  /**
   * Highlighted text in the title
   */
  highlightedText?: string;

  /**
   * Subtitle text
   */
  subtitle?: string | React.ReactNode;

  /**
   * Optional search bar rendered below the subtitle
   */
  searchBar?: React.ReactNode;

  /**
   * Music & Dances category label
   */
  musicDancesLabel?: string;

  /**
   * Personal Development category label
   */
  personalDevelopmentLabel?: string;

  /**
   * Health & Wellness category label
   */
  healthWellnessLabel?: string;
  className?: string;
}

/**
 * LandingSection component for the landing page
 *
 * @example
 * ```tsx
 * <LandingSection
 *   title="Your ultimate destination for"
 *   highlightedText="learning"
 *   subtitle="Discover and get mentored by the top rated mentors & instructors near you"
 *   musicDancesLabel="Music & Dances"
 *   personalDevelopmentLabel="Personal Development"
 *   healthWellnessLabel="Health & Wellness"
 * />
 * ```
 */
export const LandingSection: React.FC<LandingSectionProps> = ({
  title = 'Your ultimate destination for',
  highlightedText = 'learning',
  subtitle = 'Discover and get mentored by the top rated mentors & instructors near you',
  searchBar,
  musicDancesLabel = 'Music & Dances',
  personalDevelopmentLabel = 'Personal Development',
  healthWellnessLabel = 'Health & Wellness',
  className
}) => {
  return (
    <LandingWrapper disableMinHeight className={className} containerClassName="gap-3">
      {/* Left side - Text content */}
      <div className="flex w-full flex-col items-start justify-start gap-4 md:w-1/2 lg:w-[576px]">
        <h1 className="self-stretch text-3xl font-bold leading-tight text-neutral-800 sm:text-4xl sm:leading-[50.40px]">
          {title} <span className="text-primary-500">{highlightedText}</span>
        </h1>
        {typeof subtitle === 'string' ? (
          <p className="self-stretch text-base font-medium text-neutral-800 sm:text-xl">{subtitle}</p>
        ) : (
          subtitle
        )}
        {searchBar}
      </div>

      {/* Right side - Visual content */}
      <VisualSection
        musicDancesLabel={musicDancesLabel}
        personalDevelopmentLabel={personalDevelopmentLabel}
        healthWellnessLabel={healthWellnessLabel}
        photoCount={3}
      />
    </LandingWrapper>
  );
};
